import { Directive, ElementRef, Renderer2, Input, Output, EventEmitter, HostListener, OnDestroy } from '@angular/core';
import { DragDropManagerService, Draggable } from 'sharedFeatures/sharedServices/drag-drop-manager.service';
import { DomSmallUtils } from 'sharedGuiSmall/utils/dom-utils';
import { DraggEvent } from './events/drag-event';

@Directive({
    selector: '[draggabled]',
    standalone: true
})

export class DraggabledDirective implements OnDestroy, Draggable {

    dragText = $localize`Drag Text`;

    @Output() private drgstart: EventEmitter<any> = new EventEmitter();
    @Output() private drg: EventEmitter<any> = new EventEmitter();
    @Output() private drgend: EventEmitter<any> = new EventEmitter();

    @Input()
    public textFunc: Function = this.defaultTextFunc;

    @Input()
    private text: string = '';

    @Input()
    public dragData: any = new Object();

    @Input()
    private isLiveDrag: boolean = false;

    @Input()
    private onlyHorizontalDrag: boolean = false;

    @Input()
    public draggabledObject: any;

    @Input()
    public isDragAllowed: Function | undefined;

    @Input()
    private moveIncrement: number = 0;

    @Input()
    private dragConstraint: Function = this.dragConstraintDefault;

    private dragPictureTemplate: string = `<div class="b3Background p2Font g2Color dd-drag-proxy">
                                                {0}
                                            </div>`;

    private dragPic: HTMLElement | undefined;

    private isMouseDown: boolean = false;
    private mouseDownPoint: { x: number, y: number } = { x: 0, y: 0 };
    private isDragStarted: boolean = false;

    private selectListener: Function | undefined;
    private moveListener: Function | undefined;
    private mouseUpListener: Function | undefined;
    private leaveListener: Function | undefined;
    private escListener: Function | undefined;

    private dragStartDistance: number = 5;

    private xStartPos: number = 0;
    private yStartPos: number = 0;

    private deltaX: number = 0;
    private deltaY: number = 0;

    constructor(private el: ElementRef,
                private r: Renderer2,
                private ddService: DragDropManagerService
    ) {

        this.ddService.addDraggable(this);

    }

    @HostListener('mousedown', ['$event'])
    onMouseDown(ev: MouseEvent): void {

        if (this.isDragAllowed && !this.isDragAllowed(this.draggabledObject)) return;

        this.isMouseDown = true;
        this.xStartPos = 0;
        this.yStartPos = 0;
        this.deltaX = 0;
        this.deltaY = 0;

        this.mouseDownPoint = { x: ev.pageX, y: ev.pageY };

        this.selectListener = this.r.listen(document, 'selectstart', (ev) => {

            ev.stopPropagation();

            return false;

        });
        this.moveListener = this.r.listen(document, 'mousemove', this.onMouseMove.bind(this));
        this.mouseUpListener = this.r.listen(document, 'mouseup', this.onMouseUp.bind(this));
        this.leaveListener = this.r.listen(document, 'mouseleave', this.onMouseLeave.bind(this));
        this.escListener = this.r.listen(document, 'keydown.escape', this.onEscHandler.bind(this));
    }

    //@HostListener('window:mousemove', ['$event'])
    onMouseMove(ev: MouseEvent): void {

        if (!this.isMouseDown || Math.max(Math.abs(this.mouseDownPoint.x - ev.pageX), Math.abs(this.mouseDownPoint.y - ev.pageY)) < this.dragStartDistance) return;

        if (!this.isDragStarted) {
            this.isDragStarted = true;
            this.onDragStart(ev);
        }


        let topPos: number = this.onlyHorizontalDrag ? this.yStartPos : (this.isLiveDrag ? (ev.pageY - this.deltaY) : ev.pageY + 2);

        let snappedX = this.moveIncrement ? this.xStartPos + Math.round((ev.pageX + 2 - this.xStartPos) / this.moveIncrement) * this.moveIncrement : (ev.pageX + 2);
        let snappedXLive = this.moveIncrement ? this.xStartPos + Math.round((ev.pageX - this.deltaX - this.xStartPos) / this.moveIncrement) * this.moveIncrement : (ev.pageX - this.deltaX);

        let leftPos = this.isLiveDrag ? snappedXLive : snappedX;

        let dragConstraint = this.dragConstraint();

        leftPos = dragConstraint ? Math.max(leftPos, dragConstraint.left) : leftPos;

        if (dragConstraint && leftPos + DomSmallUtils.width(this.dragPic) > dragConstraint.right) leftPos = DomSmallUtils.offset(this.dragPic).left;

        DomSmallUtils.setOffset(this.dragPic, { left: leftPos, top: topPos });

        this.drg.emit(new DraggEvent(ev, this));

    }

    private onDragStart(ev: MouseEvent): void {

        let rect = this.getEl().getBoundingClientRect();

        this.dragPic = this.isLiveDrag ? this.getEl() : DomSmallUtils.generateElements(this.dragPictureTemplate.format(this.text ? this.text : this.textFunc(this.dragData)));
        this.r.appendChild(this.isLiveDrag ? this.getEl().parentNode : document.body, this.dragPic);

        ev.preventDefault();

        this.deltaX = this.mouseDownPoint.x - rect.left;
        this.deltaY = this.mouseDownPoint.y - rect.top;

        this.xStartPos = rect.left;
        this.yStartPos = rect.top;

        let leftPos = this.isLiveDrag ? (ev.pageX - this.deltaX) : ev.pageX + 2;

        let dragConstraint = this.dragConstraint();

        leftPos = dragConstraint ? Math.max(leftPos, dragConstraint.left) : leftPos;

        if (dragConstraint && leftPos + DomSmallUtils.width(this.dragPic) > dragConstraint.right) leftPos = DomSmallUtils.offset(this.dragPic).left;

        DomSmallUtils.setOffset(this.dragPic, { left: leftPos, top: this.isLiveDrag ? (ev.pageY - this.deltaY) : ev.pageY + 2 });


        this.ddService.setActiveDraggable(this);
        this.drgstart.emit(ev);

    }

    private finish(): void {

        if (!this.isMouseDown) return;

        this.isMouseDown = false;
        this.isDragStarted = false;

        if (!this.isLiveDrag) this.dragPic?.remove();

        this.ddService.setActiveDraggable(undefined);

        if (this.selectListener)
            this.selectListener();
        if (this.moveListener)
            this.moveListener();
        if (this.mouseUpListener)
            this.mouseUpListener();
        if (this.leaveListener)
            this.leaveListener();
        if (this.escListener)
            this.escListener();

    }

    //@HostListener('window:mouseup', ['$event'])
    onMouseUp(ev: MouseEvent): void {
        this.finish();
        this.drgend.emit(ev);
    }

    //@HostListener('window:mouseleave', ['$event'])
    onMouseLeave(ev: MouseEvent): void {
        this.finish();
        this.drgend.emit();
    }

    private dragConstraintDefault(): {left: number, right: number} | null {
        return null;
    }

    private defaultTextFunc(dragData: any): string {
        return dragData.text ? dragData.text : this.dragText;
    }

    public getEl(): HTMLElement {
        return this.el.nativeElement;
    }

    public isDragging(): boolean {
        return this.isDragStarted;
    }

    public getDragData(): any {
        return this.dragData;
    }

    public getXStart(): number {
        return this.xStartPos;
    }

    public getYStart(): number {
        return this.yStartPos;
    }

    public getDeltaX(): number {
        return this.deltaX;
    }

    ngOnDestroy(): void {
        this.ddService.removeDraggable(this);
    }

    private onEscHandler(evt: KeyboardEvent) {

        this.finish();
        if (this.isLiveDrag) {
            DomSmallUtils.setOffset(this.dragPic, { left: this.xStartPos, top: this.yStartPos });
        }

        this.drgend.emit();

    }
}
